import { useEffect, useState } from 'react'
import { asMessage } from '../../../shared/utils/errorDetail'
import { identityService } from '../../../shared/api'
import { parseUTC } from '../../../utils/dateUtils'

/**
 * PLATFORM ADMIN: switch modules on or off for ONE company - every module (owner, 29 Sep 2026: "the
 * on/off for each module for each tenant"), in two groups that follow two different rules
 * (shared/module_switches.py keeps them apart the same way):
 *
 *   STANDARD - the twelve every company has (Timekeeping, Projects, ...). ON unless switched off here:
 *   a company nobody has touched has every one of them, and nothing switches off by itself. Switched
 *   off, the module's tile, tab and address go for that company and its service refuses them.
 *
 *   HELD - Capital, Project Financials, Invoicing, the Office Worker. OFF until ticked here, and served
 *   only when BOTH keys are on: the ENVIRONMENT switch on the backend task definition, which is a
 *   deploy decision and is not set here, and this company's own switch, which is. So each held row
 *   says both, and a tick in an environment that has the module off cannot pass for a release.
 *
 * WHAT NEEDS WHAT (`depends_on` from the server). A module another ON module needs cannot be switched
 * off, and a module cannot be switched on while one it needs is off: the server refuses the save in
 * plain words, and each row says so in amber as soon as the boxes as they stand would be refused.
 *
 * Only a system_admin can reach the endpoints behind this (a company's own admins are refused), and the
 * server writes an audit row for every change in the same transaction as the change; each row says who
 * changed it last and when. Nothing is saved until Save is pressed, and only the modules whose box
 * changed are sent.
 */
// The company id we are looking for and the one each row carries come from different sources (the
// route/caller vs. this endpoint's own list), so a straight `===` breaks on nothing more than case or
// stray whitespace and the whole panel reads as "not found" with no way to tell why. Normalise both
// sides the same way before comparing.
const normaliseTenantId = (v) => String(v ?? '').trim().toLowerCase()

const listOf = (names) => (names.length <= 1
  ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`)

// A consequence worth saying where the box is, beyond "it disappears".
const STANDARD_NOTES = {
  forms: 'Switched off, timesheets stop waiting on required forms: nobody at this company could fill them.',
}

export default function TenantModules({ tenantId, onSaved, onDirtyChange }) {
  const [data, setData] = useState(null)        // { environment, labels, standardLabels, dependsOn, company }
  const [draft, setDraft] = useState({})         // module -> bool, only the ones changed
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)           // { type: 'ok' | 'err', text }
  const [reloadKey, setReloadKey] = useState(0)  // bumped by the retry button to re-run the load below

  // UNSAVED TICKS (review APP-8): the dialog around this screen asks before closing while any box or the
  // reason has changed and not been saved. `draft` only ever holds real changes (a box ticked back is
  // removed), so this is exactly "something would be lost".
  const dirty = Object.keys(draft).length > 0 || note.trim() !== ''
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty])   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const all = await identityService.getCompanyModules()
        if (cancelled) return
        const wanted = normaliseTenantId(tenantId)
        const company = (all.companies || []).find((c) => normaliseTenantId(c.tenant_id) === wanted) || null
        setData({
          environment: all.environment || {},
          labels: all.labels || {},
          standardLabels: all.standard_labels || {},
          dependsOn: all.depends_on || {},
          company,
        })
        if (!company) {
          setMsg({ type: 'err', text: `Could not find company ${tenantId} in this environment's module list.` })
        }
      } catch (err) {
        if (!cancelled) setMsg({ type: 'err', text: asMessage(err?.response?.data?.detail) || 'Could not load this company\'s modules.' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [tenantId, reloadKey])

  if (loading) return <p className="text-sm text-gray-500">Loading modules…</p>
  if (!data?.company) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600">
          {msg?.text || `Could not find company ${tenantId} in this environment's module list.`}
        </p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Try again
        </button>
      </div>
    )
  }

  const held = Object.keys(data.labels)
  const standard = Object.keys(data.standardLabels)
  const storedHeld = data.company.modules || {}
  const storedStandard = data.company.standard || {}
  const lastChanges = data.company.last_changes || {}
  const labelOf = (m) => data.labels[m] || data.standardLabels[m] || m
  // THE TWO RULES: a held module is on only when stored `true`; a standard one unless stored `false`.
  const storedOn = (m) => (held.includes(m) ? storedHeld[m] === true : storedStandard[m] !== false)
  const value = (m) => (m in draft ? draft[m] : storedOn(m))
  const changed = Object.keys(draft).filter((m) => draft[m] !== storedOn(m))
  const needsOf = (m) => data.dependsOn[m] || []
  const neededBy = (m) => Object.keys(data.dependsOn).filter((d) => (data.dependsOn[d] || []).includes(m))

  const toggle = (m) => {
    setMsg(null)
    const next = !value(m)
    setDraft((prev) => {
      const out = { ...prev }
      if (next === storedOn(m)) delete out[m]
      else out[m] = next
      return out
    })
  }

  const save = async () => {
    if (changed.length === 0) return
    setSaving(true)
    setMsg(null)
    try {
      const body = await identityService.setCompanyModules(
        tenantId, Object.fromEntries(changed.map((m) => [m, draft[m]])), note.trim())
      setData((prev) => ({
        ...prev,
        company: {
          ...prev.company,
          modules: body.modules,
          effective: body.effective,
          ...(body.standard ? { standard: body.standard } : {}),
          ...(body.last_changes ? { last_changes: body.last_changes } : {}),
        },
      }))
      setDraft({})
      setNote('')
      setMsg({
        type: 'ok',
        text: 'Saved. The change reaches every service within about 30 seconds; people at this company see it the next time they load the app.',
      })
      onSaved?.()
    } catch (err) {
      setMsg({ type: 'err', text: asMessage(err?.response?.data?.detail) || 'The change was not saved.' })
    } finally {
      setSaving(false)
    }
  }

  // What needs what, said on the row: plainly while the boxes are consistent, in amber once they would
  // be refused - the same rule the server applies to the save.
  const dependencyLine = (m) => {
    const needs = needsOf(m)
    const on = value(m)
    const missing = on ? needs.filter((n) => !value(n)) : []
    const dependentsOn = on ? [] : neededBy(m).filter((d) => value(d))
    if (missing.length) {
      return (
        <div className="mt-1 text-xs text-amber-700" data-testid={`depends-${m}`}>
          Needs {listOf(missing.map(labelOf))}, which {missing.length === 1 ? 'is' : 'are'} off - switch {missing.length === 1 ? 'it' : 'them'} on too, or this will not save.
        </div>
      )
    }
    if (dependentsOn.length) {
      return (
        <div className="mt-1 text-xs text-amber-700" data-testid={`depends-${m}`}>
          {listOf(dependentsOn.map(labelOf))} {dependentsOn.length === 1 ? 'needs' : 'need'} this - switch {dependentsOn.length === 1 ? 'it' : 'them'} off too, or this will not save.
        </div>
      )
    }
    if (needs.length) {
      return (
        <div className="mt-1 text-xs text-gray-500" data-testid={`depends-${m}`}>
          Needs {listOf(needs.map(labelOf))}.
        </div>
      )
    }
    return null
  }

  const lastChangeLine = (m) => {
    const last = lastChanges[m]
    if (!last) return null
    const when = last.at ? parseUTC(last.at) : null
    const day = when && !Number.isNaN(when.getTime())
      ? when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : null
    return (
      <div className="mt-1 text-xs text-gray-400" data-testid={`last-change-${m}`}>
        Switched {last.to ? 'on' : 'off'}{last.by ? ` by ${last.by}` : ''}{day ? `, ${day}` : ''}{last.note ? ` - "${last.note}"` : ''}
      </div>
    )
  }

  const checkbox = (m) => {
    const on = value(m)
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
        <input
          type="checkbox"
          checked={on}
          onChange={() => toggle(m)}
          disabled={saving}
          aria-label={`${labelOf(m)} for this company`}
        />
        {on ? 'On' : 'Off'}
      </label>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Switch any module on or off for this company. Every change is recorded with your name.
      </p>

      {standard.length > 0 && (
        <section className="space-y-2" data-testid="module-group-standard">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Standard modules - on unless switched off</h4>
            <p className="text-xs text-gray-500">
              Every company has these until you switch one off here. Switched off, it disappears for everyone
              at this company and its data stays where it is.
            </p>
          </div>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {standard.map((m) => (
              <li key={m} className="flex items-start justify-between gap-4 p-3">
                <div>
                  <div className="font-medium text-gray-900">{data.standardLabels[m]}</div>
                  <div className="text-xs text-gray-500">
                    {value(m) ? 'This company has it.' : 'Switched off for this company.'}
                  </div>
                  {STANDARD_NOTES[m] && <div className="mt-1 text-xs text-gray-500">{STANDARD_NOTES[m]}</div>}
                  {dependencyLine(m)}
                  {lastChangeLine(m)}
                </div>
                {checkbox(m)}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2" data-testid="module-group-held">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Held modules - off until ticked</h4>
          <p className="text-xs text-gray-500">
            A held module reaches this company only when it is switched on here <em>and</em> switched on for
            this environment.
          </p>
        </div>
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {held.map((m) => {
            const envOn = data.environment[m] === true
            const on = value(m)
            return (
              <li key={m} className="flex items-start justify-between gap-4 p-3">
                <div>
                  <div className="font-medium text-gray-900">{data.labels[m]}</div>
                  <div className={`text-xs ${envOn ? 'text-gray-500' : 'text-amber-700'}`}>
                    {envOn
                      ? (on ? 'This company will see it.' : 'Available in this environment; off for this company.')
                      : 'Not switched on for this environment, so nobody sees it yet — whatever this box says.'}
                  </div>
                  {/* THE ONE DEPENDENCY, said where the tick is made (readiness finding 23). Project
                      Financials' capital estimates are still Capital's rows, behind Capital's switch on
                      the server, so ticking Project Financials alone — the natural reading of "Capex
                      replaces Capital" — gives a board with no estimates and nothing saying why. Said
                      always; in amber while the boxes as they stand would leave the estimates off. */}
                  {m === 'capex' && (
                    <div className={`mt-1 text-xs ${on && !value('capital') ? 'text-amber-700' : 'text-gray-500'}`}>
                      Its capital estimates — building, pricing and awarding one — also need Capital Projects
                      switched on for this company.
                    </div>
                  )}
                  {/* AND THE OTHER WAY ROUND (review finding 17). Since the old editor was retired, a
                      capital estimate is built, priced and awarded only in Project Financials, so
                      Capital Projects on its own leaves nothing to open: its tab is retired (30 Sep 2026) and its estimates cannot be opened or
                      started. Said always; amber while the boxes as they stand would do that. */}
                  {m === 'capital' && (
                    <div className={`mt-1 text-xs ${on && !value('capex') ? 'text-amber-700' : 'text-gray-500'}`}
                         data-testid="capital-needs-capex">
                      It has no screens of its own: it lets Project Financials build, price and award capital estimates, so switch that on too.
                    </div>
                  )}
                  {dependencyLine(m)}
                  {lastChangeLine(m)}
                </div>
                {checkbox(m)}
              </li>
            )
          })}
        </ul>
      </section>

      <label className="block text-sm text-gray-700">
        Why (optional, kept with the record)
        <input
          type="text"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </label>
      {msg && (
        <p className={`text-sm ${msg.type === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || changed.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
